import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    Box,
    Paper,
    Typography,
    Chip,
    Divider
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Helper } from '../../../tools/Helper';
import { api_Routes } from '../../../tools/api_Routes';

const TicketTemplateBuilder = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { selectedIds = [], selectAllMode = false, filters = {} } = location.state || {};

    const [template, setTemplate] = useState({
        title: '',
        body: '',
        isHtml: true
    });
    const [openConfirm, setOpenConfirm] = useState(false);

    // Available template variables
    const templateVariables = [
        { key: '{{user_name}}', description: 'User full name' },
        { key: '{{user_email}}', description: 'User email address' },
        { key: '{{user_id}}', description: 'User ID' },
        { key: '{{current_date}}', description: 'Current date (YYYY-MM-DD)' }
    ];

    const modules = {
        toolbar: [
            [{ header: [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ list: 'ordered' }, { list: 'bullet' }],
            ['link'],
            ['clean']
        ],
    };

    const handleVariableInsert = (variable) => {
        const editor = document.querySelector('.ql-editor');
        const selection = window.getSelection();
        const range = selection.getRangeAt(0);
        range.insertNode(document.createTextNode(variable));
        editor.focus();
    };

    const handleSend = async () => {
        try {
            await Helper.Post({
                url: api_Routes.Users.SendBulkTicket,
                data: {
                    recipient_ids: selectAllMode ? 'ALL' : selectedIds,
                    filters: selectAllMode ? filters : null,
                    ...template
                },
                hasToken: true
            });
            navigate('/users', { state: { message: 'Emails queued for sending successfully!' } });
        } catch (error) {
            console.error('Failed to send emails:', error);
        }
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <Typography variant="h4" className="mb-6">Ticket Template Builder</Typography>

            <Paper elevation={3} className="p-6">
                <Box component="form" className="space-y-6">
                    <TextField
                        fullWidth
                        label="Ticket Title"
                        value={template.title}
                        onChange={(e) => setTemplate({...template, title: e.target.value})}
                        variant="outlined"
                    />

                    <div className="mb-4">
                        <Typography variant="subtitle1" className="mb-2">Available Variables:</Typography>
                        <div className="flex flex-wrap gap-2 mb-4">
                            {templateVariables.map((varItem) => (
                                <Chip
                                    key={varItem.key}
                                    label={`${varItem.key} - ${varItem.description}`}
                                    onClick={() => handleVariableInsert(varItem.key)}
                                    variant="outlined"
                                    clickable
                                />
                            ))}
                        </div>
                        <Typography variant="body2" color="textSecondary">
                            Click on a variable to insert it into the template
                        </Typography>
                    </div>

                    <Divider className="my-4" />

                    <Typography variant="subtitle1" className="mb-2">Ticket Body:</Typography>
                    <ReactQuill
                        theme="snow"
                        value={template.body}
                        onChange={(value) => setTemplate({...template, body: value})}
                        modules={modules}
                        className="border rounded"
                        style={{ height: '400px', marginBottom: '40px' }}
                    />

                    <div className="flex justify-end space-x-4 pt-4">
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() => navigate(-1)}
                            size="large"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => setOpenConfirm(true)}
                            size="large"
                        >
                            Review and Send
                        </Button>
                    </div>
                </Box>
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="md" fullWidth>
                <DialogTitle>Confirm Email Sending</DialogTitle>
                <DialogContent>
                    <DialogContentText className="mb-4">
                        You are about to send this email to <strong>
                        {selectAllMode ? 'all filtered users' : `${selectedIds.length} selected users`}
                    </strong>. Please review the content:
                    </DialogContentText>

                    <Paper elevation={0} className="p-4 border rounded">
                        <Typography variant="subtitle1" className="font-bold">Subject:</Typography>
                        <Typography className="mb-4">{template.subject}</Typography>

                        <Typography variant="subtitle1" className="font-bold">Body Preview:</Typography>
                        <div
                            className="ql-editor"
                            dangerouslySetInnerHTML={{ __html: template.body }}
                            style={{ border: 'none' }}
                        />
                    </Paper>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenConfirm(false)}>Cancel</Button>
                    <Button onClick={handleSend} color="primary" variant="contained">
                        Confirm Send
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default TicketTemplateBuilder;